import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ContextType } from '@philips-internal/oswald-ui-core';
import Controller, { OnBlur, OnChange, OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { MountEvent, SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Validation } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { CreateUserFromWebLaboratoryAliases, FragmentNames } from '../../codegen';
import { HttpStatus } from '../../constants/HttpStatus';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { PatientHttpService } from '../../services/patient-http-service';
import { showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import BaseController from '../BaseController';
import { UsernameValidatorUtils } from '../../utils/username-validator-utils';

@Controller({ component: CreateUserFromWebLaboratoryAliases.MODAL_CREATE_USER_FROM_WEB_LABORATORY,
  fragment: FragmentNames.CREATE_USER_FROM_WEB_LABORATORY })
class AccountCreationUserFromWebLaboratoryController extends BaseController {

  @Inject(CreateUserFromWebLaboratoryAliases.BUTTON_CONFIRM_ACTION)
    confirmButtonAPI!: ButtonAPI;

  @Inject(CreateUserFromWebLaboratoryAliases.FORM_CREATE_NEW_USER)
    formAPI!: FormAPI;

  @Inject(CreateUserFromWebLaboratoryAliases.PANEL_LOADER)
    panelLoaderAPI!: PanelAPI;

  @Inject(CreateUserFromWebLaboratoryAliases.INPUT_LOGIN)
    loginInputAPI!: InputAPI;

  @Inject(CreateUserFromWebLaboratoryAliases.LOGIN_FIELD)
    loginFieldApi!: FieldAPI;

  @Inject(CreateUserFromWebLaboratoryAliases.PASSWORD_INPUT)
    pswInputAPI!: InputAPI;

  @Inject(CreateUserFromWebLaboratoryAliases.PASSWORD_CONFIRM_INPUT)
    confirmPswInputAPI!: InputAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnClick({target: CreateUserFromWebLaboratoryAliases.BUTTON_CONFIRM_ACTION})
  onClickConfirmationButton():void{

    const body = {
      cdPessoaFisica: this.context.get('cdPessoaFisica'),
      confirmPsw: this.confirmPswInputAPI.getValue(),
      login: this.loginInputAPI.getValue(),
      psw: this.pswInputAPI.getValue()
    }

    if (this.formAPI.isValid()) {
      this.panelLoaderAPI.setVisible(true);

      PatientHttpService.post(APPLICATION_ENDPOINTS.NEW_ACCOUNT_FROM_WEB_LABORATORY,{}, { body })
        .then( ()=>this.handleSuccessCreation( ))
        .catch(
          (response) => {
            this.panelLoaderAPI.setVisible( false );
            if (response?.status === HttpStatus.CONFLICT) {
              this.context.set({usernameAlreadyExists:true});
              this.loginFieldApi.addLocalValidator('patient.username.already.exist', () => {
                return [{
                  message: MessageService.translate('patient.username.already.exist'),
                  name: 'patient.username.already.exist',
                  valid: false
                }];
              });

              setTimeout(() => {
                this.loginFieldApi.validate();
              });
            }
          }

        );
    }
  }

  @OnChange({target: CreateUserFromWebLaboratoryAliases.LOGIN_FIELD})
  onEmailChange():void{
    const usernameAlreadyExists = this.context.get('usernameAlreadyExists');
    if(usernameAlreadyExists){
      this.loginFieldApi.removeLocalValidator('patient.username.already.exist');
      this.loginFieldApi.validate();
      this.formAPI.validate();
    }
  }

  @OnBlur({target: CreateUserFromWebLaboratoryAliases.LOGIN_FIELD})
  handleFieldLoginFocusOut(): void {
    UsernameValidatorUtils.validateUsernameField(this.loginFieldApi);
    setTimeout(() => this.loginFieldApi.validate());
  }

  @OnMount({target: CreateUserFromWebLaboratoryAliases.PASSWORD_CONFIRM_FIELD})
  onMountPassword(evt:MountEvent): void {
    const pswField = evt.target as FieldAPI;
    pswField.addLocalValidator( 'password-match-validator', () => { return this.comparePassword('confirmPassword') } );

  }

  @OnValidate({target:CreateUserFromWebLaboratoryAliases.FORM_CREATE_NEW_USER})
  onValidateForm(event: ValidateEvent):void{
    this.confirmButtonAPI.setEnabled(event.valid);
  }

  handleSuccessCreation():void{
    this.panelLoaderAPI.setVisible(false);
    this.submittableFragmentContext.submit(new SubmitEvent());
    showToastNotificationSuccess('patient.web.lab.password.desativate', undefined, undefined, 'newaccount.text.successfully.activation');
  }

  comparePassword( type = 'password' ): Validation[] {
    const password = this.pswInputAPI.getValue();
    const confirmPassword = this.confirmPswInputAPI.getValue();
    const validMessage = 'newaccount.validator.password.confirm.valid';
    const notValidMessage = 'newaccount.validator.password.not.match'

    const name = 'password-match-validator'
    let message = '';
    let valid = type === 'password';

    if ( password && confirmPassword ) {
      valid = password === confirmPassword;

      if ( type !== 'password' ) {
        message = valid ? validMessage : notValidMessage;
      }
    }

    return [{
      message,
      name,
      valid
    }];
  }

}

export default AccountCreationUserFromWebLaboratoryController;